import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import {
  ServicesSubSectionContainer,
  ServicesSubSectionHeader,
} from '../../../features/services/services-subsection/services-subsection.styles';
import Button from '../../../common/button/button.component';
import { ReactComponent as MapLocationIcon } from '../../../assets/location-sparklinkg-big-icon.svg';
import MyAddressEditForm from '../my-address-form.component';
import { AddressesContainer, EmptyViewContainer } from './styles';
import CollapsableContainer from '../../../common/collapsable-container/collapsable-container.component';
import { AddressDataValues } from '../../../models/addressDataValues';
import AddressItem from './address-item.component';
import { Modals } from '../../../constants/modals';
import Spinner from '../../../common/spinner/spinner.component';

interface EmptyAddressViewProps {
  onAddAddressClick: () => void;
}

const EmptyAddressView = ({ onAddAddressClick }: EmptyAddressViewProps) => {
  const { t } = useTranslation();

  return (
    <EmptyViewContainer>
      <MapLocationIcon />
      <p>{t('address_settings_empty_state')}</p>
      <Button
        appearance='primary'
        onClick={onAddAddressClick}>
        {t('add_address')}
      </Button>
    </EmptyViewContainer>
  );
};

interface AddressGroup {
  countryCode: string;
  addresses: AddressDataValues[];
}

function groupAddressesByCountry(
  addresses: AddressDataValues[],
  getCountryLocalizedTitleByCode: (code: string) => string,
  defaultCountry: string | undefined
): AddressGroup[] {
  const groupedByCountry = addresses
    .toSorted((a, b) => {
      if (a.country === defaultCountry && b.country !== defaultCountry) {
        return -1;
      }

      if (b.country === defaultCountry && a.country !== defaultCountry) {
        return 1;
      }

      const aName = getCountryLocalizedTitleByCode(a.country);
      const bName = getCountryLocalizedTitleByCode(b.country);

      return aName.localeCompare(bName);
    })
    .reduce<Record<string, AddressDataValues[]>>((acc, address) => {
      if (!acc[address.country]) {
        acc[address.country] = [];
      }

      acc[address.country].push(address);
      return acc;
    }, {});

  return Object.entries(groupedByCountry).map(
    ([countryCode, groupedCountryAddresses]): AddressGroup => ({
      countryCode,
      addresses: groupedCountryAddresses.toSorted(
        (a, b) => a.title?.localeCompare(b.title ?? '') ?? 0
      ),
    })
  );
}

const MyAddressMultiple = () => {
  const {
    userStore: {
      getAddresses,
      updateSenderAddress,
      deleteSenderAddress,
      senderAddresses,
      isLoadingAddresses,
      addressesInUpdateState,
    },
    localizationsStore: { selectedCountry, getCountryLocalizedTitleByCode },
    modalStore: { open, close },
    commonStore: { toastSuccess, toastInfo },
  } = useStore();

  const { t } = useTranslation();

  const [showEditForm, setShowEditForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState<
    AddressDataValues | undefined
  >();
  const [expandedCountry, setExpandedCountry] = useState<string | undefined>();

  useEffect(() => {
    getAddresses();
  }, [getAddresses]);

  const isAnyAddress = senderAddresses.length > 0;

  const groupedCountries = useMemo(
    () =>
      groupAddressesByCountry(
        senderAddresses,
        getCountryLocalizedTitleByCode,
        selectedCountry?.country_code
      ),
    [
      senderAddresses,
      getCountryLocalizedTitleByCode,
      selectedCountry?.country_code,
    ]
  );

  const handleDeleteItem = async (address: AddressDataValues) => {
    close(Modals.DELETE_ITEM);
    const success = await deleteSenderAddress(address.id);
    if (success) {
      toastSuccess(t('toast_address_deleted'));
    }
  };

  const handleDeleteClick = (address: AddressDataValues) => {
    open({
      id: Modals.DELETE_ITEM,
      name: Modals.DELETE_ITEM,
      props: {
        onApprove: () => handleDeleteItem(address),
        cancelBtnText: 'deny',
        title: 'delete_address_title',
        text: 'delete_address_description',
      },
    });
  };

  const getContent = () => {
    if (isLoadingAddresses) {
      return <Spinner />;
    }

    if (showEditForm) {
      return (
        <MyAddressEditForm
          address={editingAddress}
          onSubmit={(address) => {
            setExpandedCountry(address.country);
          }}
          onCancel={() => {
            setEditingAddress(undefined);
            setShowEditForm(false);
          }}
        />
      );
    }

    if (!isAnyAddress) {
      return (
        <EmptyAddressView
          onAddAddressClick={() => {
            setEditingAddress(undefined);
            setShowEditForm(true);
          }}
        />
      );
    }

    return (
      <AddressesContainer>
        {groupedCountries.map(({ countryCode, addresses }) => (
          <CollapsableContainer
            key={countryCode}
            title={
              <p className='flag-emoji-font'>
                {getCountryLocalizedTitleByCode(countryCode, true)}
              </p>
            }
            isOpen={expandedCountry === countryCode}
            onExpandClick={(isOpen) =>
              isOpen
                ? setExpandedCountry(undefined)
                : setExpandedCountry(countryCode)
            }>
            {addresses.map((x) => (
              <AddressItem
                key={x.id}
                address={x}
                isDisabled={addressesInUpdateState.length > 0}
                isLoading={addressesInUpdateState.includes(x.id)}
                onSelect={async () => {
                  if (x.is_default) return;

                  if (await updateSenderAddress({ ...x, is_default: true })) {
                    toastInfo(
                      t('toast_address_default_selected', {
                        addressName: x.title,
                      })
                    );
                  }
                }}
                onDelete={() => handleDeleteClick(x)}
                onEdit={() => {
                  setEditingAddress(x);
                  setShowEditForm(true);
                }}
              />
            ))}
          </CollapsableContainer>
        ))}
        <Button
          appearance='text'
          icon={{ glyph: 'plus' }}
          onClick={() => {
            setEditingAddress(undefined);
            setShowEditForm(true);
          }}>
          {t('add_new_address')}
        </Button>
      </AddressesContainer>
    );
  };

  return (
    <ServicesSubSectionContainer>
      <ServicesSubSectionHeader $isEditable={false}>
        {t('my_address')}
      </ServicesSubSectionHeader>
      {getContent()}
    </ServicesSubSectionContainer>
  );
};

export default observer(MyAddressMultiple);
